import { Geotab } from "@fairfleet/geotab";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { IbisIdData, IBIS_ID_ADDIN_ID } from "../models/IbisIdData";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { CastedAddInData, useRawAddinData } from "@/hooks/addins/useAddinData";

/**
 * Upserts IBIS ID AddIn data **without** compression so the
 * Python ibis-driver-sync service can read it as plain JSON.
 */
async function upsertIbisId(
  geotabClient: Geotab,
  details: IbisIdData,
  previous: CastedAddInData<IbisIdData>[],
): Promise<boolean> {
  let method = "Add";
  const entity: { id?: string; addInId: string; details: IbisIdData } = {
    addInId: IBIS_ID_ADDIN_ID,
    details,
  };

  try {
    const existing = previous.find(item => item.details.userId === details.userId);
    if (existing) {
      method = "Set";
      entity.id = existing.id;
    }
    await geotabClient.call(method, { typeName: "AddInData", entity });
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

export function useUpdateIbisId() {
  const queryClient = useQueryClient();
  const { geotabClient, activeSessionId } = useGeotabClient();
  const { data: previous = [] } = useRawAddinData<IbisIdData>(IBIS_ID_ADDIN_ID);

  const queryKey = ["addinData", IBIS_ID_ADDIN_ID, activeSessionId];

  return useMutation({
    mutationFn: async (details: IbisIdData) =>
      upsertIbisId(geotabClient, details, previous),
    onMutate: async (newDetails: IbisIdData) => {
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<CastedAddInData<IbisIdData>[]>(queryKey) ?? [];

      const exists = previousData.some(item => item.details.userId === newDetails.userId);
      const updatedData = exists
        ? previousData.map(item =>
            item.details.userId === newDetails.userId ? { ...item, details: newDetails } : item,
          )
        : [
            ...previousData,
            { id: `temp-${Date.now()}`, addInId: IBIS_ID_ADDIN_ID, details: newDetails },
          ];

      queryClient.setQueryData<CastedAddInData<IbisIdData>[]>(queryKey, updatedData);

      return { previousData };
    },
    onError: (_, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData<CastedAddInData<IbisIdData>[]>(queryKey, context.previousData);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
